import React from "react";
import styled from "styled-components";
import { Box, Heading, Hidden, Text, TileLink } from "@origin-digital/ods-core";
import { IconChevronRightSmall } from "@origin-digital/ods-icons";
import type { OtherCategoryCardProps } from "./types";
import { SubLinkList } from "./CategoryCard";

const OtherCardTitleRow = ({ name }: { name: string }) => (
  <Box
    display="flex"
    width="full"
    flexDirection="row"
    alignItems="center"
    justifyContent="space-between"
    gap="medium"
  >
    <Heading component="h3" variant="h2">
      {name}
    </Heading>
    <Box display="flex" position="relative" left="xxsmall">
      <IconChevronRightSmall
        variant="solid"
        size="xsmall"
        color="neutralDark"
      />
    </Box>
  </Box>
);

type CardHeaderContentProps = {
  name: string;
  description: string;
  iconUrl: string | null;
};

const WideCardHeaderContent = ({
  name,
  description,
  iconUrl,
}: CardHeaderContentProps) => (
  <Box display="flex" width="full" flexDirection="column" gap="small">
    <Box
      display="flex"
      width="full"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <Box display="flex" flexDirection="row" alignItems="center" gap="small">
        <Heading component="h3" variant="h2">
          {name}
        </Heading>
        <Box display="flex" position="relative" left="xxsmall">
          <IconChevronRightSmall
            variant="solid"
            size="xsmall"
            color="neutralDark"
          />
        </Box>
      </Box>
      {iconUrl && <img src={iconUrl} width="48" height="48" alt="" />}
    </Box>
    <Hidden below="lg">
      <Text tone="neutralDark">{description}</Text>
    </Hidden>
  </Box>
);

const NarrowCardHeaderContent = ({
  name,
  description,
  iconUrl,
}: CardHeaderContentProps) => (
  <>
    {iconUrl && (
      <Box display="flex" width="full" marginBottom="small">
        <img src={iconUrl} width="32" height="32" alt="" />
      </Box>
    )}
    <Box display="flex" width="full" flexDirection="column" gap="small">
      <OtherCardTitleRow name={name} />
      <Hidden below="lg">
        <Text tone="neutralDark">{description}</Text>
      </Hidden>
    </Box>
  </>
);

const OtherCardHeader = ({
  name,
  description,
  iconUrl,
  isWide,
}: CardHeaderContentProps & { isWide?: boolean }) => (
  <Box
    display="flex"
    width="full"
    flexDirection="column"
    paddingX={{ xs: "large", sm: "xlarge" }}
    height="full"
  >
    {isWide ? (
      <WideCardHeaderContent
        name={name}
        description={description}
        iconUrl={iconUrl}
      />
    ) : (
      <NarrowCardHeaderContent
        name={name}
        description={description}
        iconUrl={iconUrl}
      />
    )}
  </Box>
);

const OtherCardBody = ({
  category,
  iconUrl,
  isWide,
  subLinks,
}: {
  category: OtherCategoryCardProps["category"];
  iconUrl: string | null;
  isWide?: boolean;
  subLinks: OtherCategoryCardProps["category"]["subLinks"];
}) => (
  <Box display="flex" width="full" flexDirection="column" paddingY="xlarge">
    <OtherCardHeader
      name={category.name}
      description={category.description}
      iconUrl={iconUrl}
      isWide={isWide}
    />
    {/* Phantom — preserves card height; real SubLinkList rendered outside TileLink */}
    <div
      aria-hidden="true"
      style={{ visibility: "hidden", pointerEvents: "none" }}
    >
      <SubLinkList subLinks={subLinks ?? []} idPrefix="phantom" />
    </div>
  </Box>
);

export const OtherCategoryCard: React.FC<OtherCategoryCardProps> = ({
  category,
  isWide,
}) => {
  const subLinks = category.subLinks ?? [];
  return (
    <Box
      display="flex"
      height="full"
      width="full"
      position="relative"
      className="other-category-card-container"
      data-id={`other-category-card-${category.slug}`}
    >
      <TileLink
        href={category.url}
        hideChevron
        dataId={`other-category-tile-${category.slug}`}
        trackingType="button"
        trackingLabel={`Category:${category.name}`}
        trackingData={{
          label: `Category:${category.name}`,
          componentType: "button",
        }}
        cardProps={{
          borderRadius: "medium",
          backgroundColor: "grey50",
          paddingXSpecific: "none",
          paddingYSpecific: "none",
          height: "full",
        }}
      >
        <OtherCardBody
          category={category}
          iconUrl={category.icon}
          isWide={isWide}
          subLinks={subLinks}
        />
      </TileLink>
      {/* Real SubLinkList — sibling to TileLink so parent onClickCapture never fires */}
      <Box
        position="absolute"
        style={{ bottom: 0, left: 0, right: 0 }}
        paddingBottom="xlarge"
      >
        <SubLinkList
          subLinks={subLinks}
          idPrefix={`other-sub-link-${category.slug}`}
        />
      </Box>
    </Box>
  );
};
